import React, { useEffect, useState } from "react";
import { Table, Row, Col, Form, Button, message, Card, Statistic, Alert, Tag, Typography } from "antd";
import { AppDispatch } from "../../helpers/appDispatch";
import { useDispatch } from "react-redux";
import {
  bulkPaymentRequest,
  clearStates,
  uploadedFilePreview,
  submitToCheckerRequest,
  submitToCheckerSuccess,
  submitToCheckerFailure,
} from "../../store/bulk-payment";
import { appSelector } from "../../helpers/appSelector";
import {
  BulkPayload,
  FilePreview,
} from "../../interfaces/BulkPayment";
import { LoadingOutlined, ArrowLeftOutlined, DollarOutlined, FileTextOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

interface Props {
  loading: boolean;
  translate: any;
  onBack: () => void;
}

interface TableData {
  fileId: number;
  amount: number;
  msisdn: string;
  currency: string;
  channel?: string;
}

const DataTable: React.FC<Props> = ({
  loading: initialLoading,
  translate,
  onBack
}) => {
  // Track visibility of alert separately from submission status
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [processedFileId, setProcessedFileId] = useState<number | null>(null);

  // Calculate totals by currency
  const calculateTotalsByCurrency = (data: TableData[]) => {
    const totals: { [key: string]: number } = {};
    data.forEach(item => {
      if (!totals[item.currency]) {
        totals[item.currency] = 0;
      }
      totals[item.currency] += item.amount;
    });
    return totals;
  };

  // Calculate transactions count by channel
  const calculateTransactionsByChannel = (data: TableData[]) => {
    const channelCounts: { [key: string]: number } = {};
    data.forEach(item => {
      const channel = item.channel || 'Unknown';
      if (!channelCounts[channel]) {
        channelCounts[channel] = 0;
      }
      channelCounts[channel] += 1;
    });
    return channelCounts;
  };

  const columns: any[] = [
    {
      title: `${translate("MSISDN")}`,
      dataIndex: "msisdn",
      key: "msisdn",
      align: "center",
      render: (pageName: string) => (
        <span style={{ color: "#35b9e6" }}>
          {pageName}
        </span>
      ),
    },
    {
      title: `${translate("general.currency")}`,
      dataIndex: "currency",
      key: "currency",
      align: "center",
    },
    {
      title: `${translate("general.amount")}`,
      dataIndex: "amount",
      key: "amount",
      align: "center",
    },
    {
      title: `${translate("general.channel")}`,
      dataIndex: "channel",
      key: "channel",
      align: "center",
      render: (channel: string) => channel || '-',
    },
  ];

  const dispatch: AppDispatch = useDispatch();
  const { 
    fileId, 
    pages, 
    filePreviewSuccess, 
    uploadSuccess,
    error,
    submitToCheckerError, 
    isSubmitting,
    success,
    submitToCheckerSuccess, 
  } = appSelector((state) => state.bulkPayment);

  let data: TableData[] = [];
  let totalTransactions = 0;

  for (let page of pages) {
    if (page) {
      data.push({
        fileId: page.fileId,
        amount: page.amount,
        msisdn: page.msisdn,
        currency: page.currency,
        channel: page.channel,
      });
      totalTransactions += 1;
    }
  }

  const totalsByCurrency = calculateTotalsByCurrency(data);
  const transactionsByChannel = calculateTransactionsByChannel(data);

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(uploadedFilePreview(fileId));
    
    // If this is a new file (fileId changed), reset submission states
    if (processedFileId !== fileId) {
      setProcessedFileId(fileId);
      dispatch({ type: '@bulk-payment/SUBMIT_TO_CHECKER_SUCCESS', payload: null });
      dispatch({ type: '@bulk-payment/SUBMIT_TO_CHECKER_FAILURE', payload: null });
    }
  }, [fileId, dispatch, processedFileId]);

  // Clear only the alert visibility after a timeout
  useEffect(() => {
    if (submitToCheckerError) {
      setShowErrorAlert(true);
      const timer = setTimeout(() => {
        setShowErrorAlert(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [submitToCheckerError]);

  // Show success alert but don't reset submitToCheckerSuccess state
  useEffect(() => {
    if (submitToCheckerSuccess) {
      setShowSuccessAlert(true);
      const timer = setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [submitToCheckerSuccess]);

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      // Reset submission states when leaving the component
      dispatch({ type: '@bulk-payment/SUBMIT_TO_CHECKER_SUCCESS', payload: null });
      dispatch({ type: '@bulk-payment/SUBMIT_TO_CHECKER_FAILURE', payload: null });
    };
  }, [dispatch]);

  const submitPaymentToChecker = () => {
    dispatch(submitToCheckerRequest(fileId));
  };

  const isButtonDisabled = isSubmitting || !filePreviewSuccess || submitToCheckerSuccess;

  return (
    <div>
      <Row style={{ position: "relative", marginBottom: "20px" }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
        >
          {t("general.back")}
        </Button>
      </Row>

      {/* Show error as Alert */}
      {submitToCheckerError && showErrorAlert && (
        <Alert
          message={translate("errors.submitToChecker")}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => {
            setShowErrorAlert(false);
          }}
        />
      )}

      {/* Show success as Alert */}
      {submitToCheckerSuccess && showSuccessAlert && (
        <Alert
          message={translate("success.submitToChecker")}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => {
            setShowSuccessAlert(false);
          }}
        />
      )}

      <Row gutter={16} className="stats-summary-row">
        {Object.entries(totalsByCurrency).map(([currency, amount], index) => {
          const totalCards = Object.keys(totalsByCurrency).length + 1;
          const colSpan = Math.floor(24 / totalCards);
          
          return (
            <Col span={colSpan} key={currency}>
              <Card className="stat-card amount-card">
                <div className="stat-icon">
                  <DollarOutlined />
                </div>
                <Statistic
                  title={<span className="stat-title">{t("general.totalAmount")} ({currency})</span>}
                  value={amount}
                  precision={2}
                  prefix={currency}
                  className="stat-value"
                />
                <div className="stat-background"></div>
              </Card>
            </Col>
          );
        })}
        <Col span={Math.floor(24 / (Object.keys(totalsByCurrency).length + 1))}>
          <Card className="stat-card transactions-card">
            <div className="stat-icon">
              <FileTextOutlined />
            </div>
            <Statistic
              title={<span className="stat-title">{t("general.totalTransactions")}</span>}
              value={totalTransactions}
              className="stat-value"
            />
            <div className="stat-background"></div>
          </Card>
        </Col>
      </Row>

      {/* Channel Transaction Pills */}
      {/* {Object.keys(transactionsByChannel).length > 0 && (
        <Row style={{ marginTop: 8, marginBottom: 8 }}>
          <Col span={24}>
            <div style={{ 
              marginLeft: '10px',
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '30px', 
              justifyContent: Object.keys(transactionsByChannel).length > 5 ? 'space-between' : 'flex-start',
              alignItems: 'center'
            }}>
              {Object.entries(transactionsByChannel).map(([channel, count]) => (
                <div key={channel} style={{ 
                  display: 'flex', 
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                }}>
                  <div style={{
                    backgroundColor: '#d3d3d3',
                    color: '#000000',
                    padding: '6px 12px',
                    fontWeight: 500,
                    fontSize: '14px',
                    borderTopLeftRadius: '12px',
                    borderBottomLeftRadius: '12px',
                  }}>
                    {channel}
                  </div>
                  <div style={{
                    backgroundColor: 'white',                 
                    color: '#000000',
                    padding: '6px 12px',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    borderTopRightRadius: '12px',
                    borderBottomRightRadius: '12px',
                    border: '1px solid #d3d3d3',
                    borderLeft: 'none',
                  }}>
                    {count}
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      )} */}

      <Row gutter={20}>
        <Col span={24}>
          <div className="table-padding">
            <Table
              dataSource={data}
              columns={columns}
              bordered
              pagination={{
                hideOnSinglePage: true,
                total: data.length,
                showTotal: (total, range) => {
                  const tran = translate(`general.pagination`);
                  let t = tran.replace(`%d`, `${range[0]} - ${range[1]}`);
                  let s = t.replace(`%s`, total);
                  return s;
                },
              }}
              loading={!filePreviewSuccess}
            />
          </div>
        </Col>
      </Row>
      <Form.Item className="btn-input">
        <Button
          type="primary"
          className="login-btn"
          onClick={submitPaymentToChecker}
          loading={isSubmitting}
          disabled={isButtonDisabled}
          icon={isSubmitting ? <LoadingOutlined /> : null}
        >
          {isSubmitting ? translate("general.submitting") : translate("general.processPayment")}
        </Button>
      </Form.Item>
    </div>
  );
};

export default DataTable;